// Route connecting all the components after signUp or login having sidebar
//  for navigation.

// Hooks
import React, { useEffect, useState } from "react";

// Components
import Sidebar from "../../Shared/sidebar/sidebar";
import IdleTimerContainer from "../../Shared/inactivity-tracker/inactivityTracker";
import WarningBar from "./dashboard/warning-bar/warningBar";
import TopBar from "../../Shared/top-bar/TopBar";

// Styles
import Styles from "./application.module.scss";

// Functions
import { batchPaths } from "../../api/batch";
import { callApi } from "../../api/fetch";
import { extractBatchTypesPayload } from "./batch-service/batchApiMappers";
import { Outlet, useNavigate } from "react-router-dom";
import { logout } from "../../utils/logout";
import {
  getStoredUserId,
  getUserDisplayEmail,
  isAuthSessionValid,
  syncStoredOrganisationId,
} from "../../utils/authSession";
import type { ApplicationOrgProfile } from "./applicationContext";
import { normalizeOnboardingStatus } from "../../constants/onboardingStatuses";
import {
  findOrgWallet,
  readOrgPlanType,
  unwrapOrgSummary,
} from "../../utils/dashboardMappers";

const plan_types = {
  PREPAID: "PREPAID",
  POSTPAID: "POSTPAID",
};

function Application() {
  const org_id = localStorage.getItem("organisation_id");
  const user: any = localStorage.getItem("@user");
  const history = useNavigate();

  const [toggleCollapsedView, setToggleCollapsedView] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [onboardingStatus, setOnboardingStatus] = useState("");
  const [orgProfile, setOrgProfile] = useState<ApplicationOrgProfile | null>(
    null
  );
  const [batchActivated, setBatchActivated] = useState(false);
  const [accountBalance, setAccountBalance] = useState<number>();
  const [alertBalance, setAlertBalance] = useState<number>();
  const [userDetails, setUserDetails] = useState<any>({});
  const [planType, setPlanType] = useState<string>("");
  const [userInfo, setUserInfo] = useState<any>(null);
  const [primarySandboxWallet, setPrimarySandboxWallet] = useState<any>(null);

  // Check if user is authenticated - redirect to login if localStorage is cleared
  useEffect(() => {
    if (!isAuthSessionValid() || !user || !org_id) {
      localStorage.clear();
      history("/login");
      return;
    }

    // Parse user info safely
    try {
      const parsedUser = user ? JSON.parse(user) : null;
      if (!parsedUser || !getStoredUserId(parsedUser)) {
        localStorage.clear();
        history("/login");
        return;
      }
      setUserInfo(parsedUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error parsing user data:", error);
      localStorage.clear();
      history("/login");
    }
  }, [user, org_id, history]);

  // getting org info api
  useEffect(() => {
    if (
      !isAuthenticated ||
      !org_id ||
      !userInfo ||
      !getStoredUserId(userInfo)
    ) {
      return;
    }

    callApi(`/organisation`, { method: "GET" })
      .then((response) => {
        const { onboarding_status, organisation_id, country_code } =
          response as {
            onboarding_status?: string;
            organisation_id?: string;
            country_code?: string;
          };
        syncStoredOrganisationId(organisation_id);
        const profile: ApplicationOrgProfile = {
          onboarding_status: normalizeOnboardingStatus(onboarding_status),
          country_code: country_code || "",
          plan_type: readOrgPlanType(response),
          organisation_id,
        };
        setOrgProfile(profile);
        setOnboardingStatus(profile.onboarding_status);
        setPlanType(profile.plan_type);
        if (profile.country_code) {
          localStorage.setItem("country_code", profile.country_code);
        }
        if (!profile.plan_type) {
          const planOrgId = organisation_id || org_id;
          if (planOrgId) {
            callApi(`/organisations/${planOrgId}/plan-info`, { method: "GET" })
              .then((planInfo) => {
                const planTypeFromInfo = readOrgPlanType(planInfo);
                if (!planTypeFromInfo) {
                  return;
                }
                setPlanType(planTypeFromInfo);
                setOrgProfile((prev) =>
                  prev ? { ...prev, plan_type: planTypeFromInfo } : prev
                );
              })
              .catch(() => {});
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load organisation profile", err);
      });

    callApi(batchPaths.types, {
      method: "GET",
    })
      .then((response) => {
        const types = extractBatchTypesPayload(response);
        if (types.length) {
          setBatchActivated(true);
        }
      })
      .catch(() => {
        setBatchActivated(false);
      });

    callApi(`/user`, {
      method: "GET",
    })
      .then((response) => {
        if (response) {
          setUserDetails(response);
        }
      })
      .catch(() => {
        setUserDetails(null);
      });
  }, [org_id, userInfo, isAuthenticated]);

  useEffect(() => {
    const sessionEmail = getUserDisplayEmail(userInfo);
    const profileEmail = getUserDisplayEmail(userDetails);
    if (profileEmail && sessionEmail) {
      if (
        sessionEmail.toLowerCase().trim() !== profileEmail.toLowerCase().trim()
      ) {
        localStorage.removeItem("organisation_id");
        logout({ redirectToLogin: true });
      }
    }
  }, [userDetails, userInfo, history]);

  useEffect(() => {
    async function getOrgBalance() {
      try {
        const res = await callApi(`/organisations/${org_id}/summary`, {
          method: "GET",
        });

        const { wallets } = unwrapOrgSummary(res);
        const primaryProductionWallet = findOrgWallet(
          wallets,
          "primary",
          "production"
        );

        setAccountBalance(Number(primaryProductionWallet?.wallets_balance) || 0);
        setAlertBalance(Number(primaryProductionWallet?.alert_balance) || 0);
        setPrimarySandboxWallet(
          findOrgWallet(wallets, "primary", "sandbox")
        );
      } catch (err) {
        console.log(err);
      }
    }

    if (org_id) {
      getOrgBalance();
    }
  }, [org_id]);

  // Don't render if not authenticated (will redirect in useEffect)
  if (!isAuthenticated || !userInfo) {
    return null;
  }

  return (
    <div className="container-fluid p-0">
      <div className={Styles.wrapper}>
        <div
          className={`d-none d-xl-block ${
            toggleCollapsedView ? Styles.collapseSideBar : Styles.expandSideBar
          }`}
          style={{ position: "relative" }}
        >
          <Sidebar
            collapsedView={toggleCollapsedView}
            toggleCollapseSideBar={() =>
              setToggleCollapsedView(!toggleCollapsedView)
            }
            onboardingStatus={onboardingStatus}
            batchActivated={batchActivated}
          />
        </div>
        <div
          className={`${
            toggleCollapsedView
              ? Styles.collapsePlayground
              : Styles.expandPlayground
          }`}
        >
          <TopBar
            userDetails={userDetails}
            primarySandboxWallet={primarySandboxWallet}
            onboardingStatus={onboardingStatus}
          />
          <IdleTimerContainer>
            <Outlet context={{ orgProfile }} />
          </IdleTimerContainer>
          {planType?.toUpperCase() === plan_types?.PREPAID &&
          accountBalance !== undefined &&
          accountBalance !== null &&
          alertBalance &&
          accountBalance < alertBalance ? (
            <WarningBar>
              <p>Your account Balance is low!</p>
            </WarningBar>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Application;
